import { alwaysRetainsIrpf } from '@/utils/irpfPolicy';
import { formatLocalDate } from '@/utils/localDateUtils';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Inmueble, InmuebleEstado, DestinoInmueble } from '@/types';
import inmuebleService from '@/services/inmuebleService';
import contractService from '@/services/contractService';
import MainLayout from '@/components/layout/MainLayout';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import { getClientFullName } from '@/utils/clientUtils';
import { useAuth } from '@/contexts/AuthContext';
import { useDialog } from '@/hooks/useDialog';

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { showAlert } = useDialog();
  const [property, setProperty] = useState<Inmueble | null>(null);
  const [loading, setLoading] = useState(true);
  const [showHandoverModal, setShowHandoverModal] = useState(false);
  const [handoverForm, setHandoverForm] = useState({ fecha_entrega: '', notas: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (!id) return;
        const res = await inmuebleService.getInmuebleById(Number(id));
        setProperty((res as any).data || res);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const getStatusColor = (estado: string) => {
    const colors: Record<string, string> = {
      [InmuebleEstado.LIBRE]: 'bg-green-100 text-green-800 border-green-200',
      [InmuebleEstado.ALQUILADO]: 'bg-blue-100 text-blue-800 border-blue-200',
    };
    return colors[estado] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (estado: string) => {
    const icons: Record<string, string> = {
      [InmuebleEstado.LIBRE]: '✅',
      [InmuebleEstado.ALQUILADO]: '🏠',
    };
    return icons[estado] || '🏢';
  };

  const formatCurrency = (amount: number | null | undefined, currency: string = 'UYU') => {
    if (!amount) return '-';
    return `${currency} ${Number(amount).toLocaleString('es-UY')}`;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return formatLocalDate(dateString, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleHandoverSubmit = async () => {
    if (!property?.contrato_activo?.id || !handoverForm.fecha_entrega) {
      showAlert({
        type: 'warning',
        title: 'Campo requerido',
        message: 'Por favor complete la fecha de entrega',
      });
      return;
    }

    try {
      setSubmitting(true);
      await contractService.handoverProperty(property.contrato_activo.id, handoverForm);
      showAlert({
        type: 'success',
        title: 'Éxito',
        message: 'Inmueble entregado exitosamente',
      });
      setShowHandoverModal(false);
      setHandoverForm({ fecha_entrega: '', notas: '' });
      // Reload property data
      const res = await inmuebleService.getInmuebleById(Number(id));
      setProperty((res as any).data || res);
    } catch (error: any) {
      showAlert({
        type: 'error',
        title: 'Error',
        message: error.response?.data?.message || 'Error al entregar el inmueble',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-96 bg-gray-200 rounded"></div>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!property) {
    return (
      <MainLayout>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🏢</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Inmueble no encontrado</h3>
            <Button onClick={() => navigate(-1)}>Volver</Button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-bold text-gray-900">{property.direccion}</h1>
              <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(property.estado)}`}>
                <span>{getStatusIcon(property.estado)}</span>
                {property.estado}
              </span>
            </div>
            <p className="text-gray-600 flex items-center gap-2">
              <span>📍</span>
              {property.ciudad}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => navigate(-1)}>
              Volver
            </Button>
            {hasPermission('properties', 'write') && (
              <Button onClick={() => navigate(`/properties/${id}/edit`)}>
                Editar Inmueble
              </Button>
            )}
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Property Details Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Información del Inmueble</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">ID</h3>
                  <p className="text-gray-900 font-mono">#{property.id}</p>
                </div>

                {property.padron && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-500 mb-1">No. Padrón</h3>
                    <p className="text-gray-900 font-mono">{property.padron}</p>
                  </div>
                )}

                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Destino</h3>
                  <p className="text-gray-900">
                    {property.destino_inmueble === DestinoInmueble.COMERCIAL && '🏢 Comercial'}
                    {property.destino_inmueble === DestinoInmueble.PARTICULAR && '🏠 Casa/Habitacion'}
                    {!property.destino_inmueble && '🏢 Comercial'}
                  </p>
                </div>
              </div>

              {property.descripcion && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Descripción</h3>
                  <p className="text-gray-700 whitespace-pre-wrap">{property.descripcion}</p>
                </div>
              )}
            </div>

            {/* Location Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Ubicación</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">🏘️</div>
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-500">Dirección Completa</h3>
                    <p className="text-gray-900 font-medium">{property.direccion}</p>
                    <p className="text-gray-600 text-sm mt-1">{property.ciudad}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contract History */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Historial de Contratos</h2>
              {property.contratos && property.contratos.length > 0 ? (
                <div className="space-y-3">
                  {property.contratos.map((contrato) => (
                    <div
                      key={contrato.id}
                      onClick={() => navigate(`/contracts/${contrato.id}`)}
                      className="p-4 border border-gray-200 rounded-lg hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-gray-900">{contrato.numero_contrato}</span>
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                contrato.estado === 'ACTIVO'
                                  ? 'bg-green-100 text-green-800'
                                  : contrato.estado === 'FINALIZADO'
                                  ? 'bg-gray-100 text-gray-800'
                                  : contrato.estado === 'RESCINDIDO'
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {contrato.estado}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Inquilino:</span> {contrato.inquilino_nombre}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDate(contrato.fecha_inicio)} - {formatDate(contrato.fecha_fin)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {formatCurrency(contrato.monto_alquiler_inquilino, contrato.moneda_inquilino)}
                          </p>
                          <p className="text-xs text-gray-500">Inquilino</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-500 pt-2 border-t border-gray-100">
                        <span>📋 {contrato.tipo_contrato}</span>
                        {contrato.inquilino_documento && (
                          <>
                            <span>•</span>
                            <span>{contrato.inquilino_tipo_documento} {contrato.inquilino_documento}</span>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <div className="text-4xl mb-2">📋</div>
                  <p className="text-sm">No hay contratos asociados a este inmueble</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Quick Stats & Actions */}
          <div className="space-y-6">
            {/* Owner Info Card */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border-2 border-blue-200">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center text-2xl">
                  🏠
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Propietarios ({property.propietarios?.length || 0})
                </h3>
              </div>

              {property.propietarios && property.propietarios.length > 0 ? (
                <div className="grid grid-cols-1 gap-3">
                  {property.propietarios.map((propietario) => (
                    <div
                      key={propietario.id}
                      onClick={() => navigate(`/clients/${propietario.id}`)}
                      className="bg-white rounded-lg p-4 border border-blue-200 hover:border-blue-400 hover:shadow-md transition-all cursor-pointer"
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-lg">
                          👤
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-gray-900 font-medium truncate">
                            {getClientFullName(propietario)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {propietario.tipo_documento} {propietario.documento}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-2 text-sm">
                        {propietario.porcentaje_propiedad !== undefined && (
                          <div className="flex items-center justify-between bg-blue-50 rounded px-3 py-2">
                            <span className="text-gray-700 font-medium">Propiedad:</span>
                            <span className="text-blue-700 font-bold">{propietario.porcentaje_propiedad}%</span>
                          </div>
                        )}

                        <div className="flex flex-wrap gap-2">
                          {propietario.no_comision ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              ✓ Sin comisión
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                              Con comisión
                            </span>
                          )}

                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            {alwaysRetainsIrpf(propietario) ? 'Siempre Retener IRPF' : 'IRPF automático según mínimo'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-lg p-6 text-center">
                  <div className="text-4xl mb-2">👥</div>
                  <p className="text-gray-500 text-sm">Sin propietarios asignados</p>
                </div>
              )}
            </div>

            {/* Current Tenant Info Card */}
            {property.contrato_activo && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Inquilino Actual</h3>
                <div className="space-y-3">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        <span className="text-xl">👤</span>
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{property.contrato_activo.inquilino_nombre}</p>
                        {property.contrato_activo.inquilino_documento && (
                          <p className="text-sm text-gray-500">
                            {property.contrato_activo.inquilino_tipo_documento} {property.contrato_activo.inquilino_documento}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(`/clients/${property.contrato_activo!.inquilino_id}`)}
                      >
                        Ver
                      </Button>
                    </div>
                    <div className="ml-13 space-y-1 text-sm">
                      {property.contrato_activo.inquilino_email && (
                        <p className="text-gray-700">
                          <span className="font-medium">Email:</span> {property.contrato_activo.inquilino_email}
                        </p>
                      )}
                      {property.contrato_activo.inquilino_telefono && (
                        <p className="text-gray-700">
                          <span className="font-medium">Teléfono:</span> {property.contrato_activo.inquilino_telefono}
                        </p>
                      )}
                      <div className="pt-2 mt-2 border-t border-gray-200">
                        <p className="text-gray-700">
                          <span className="font-medium">Contrato:</span> {property.contrato_activo.numero_contrato}
                        </p>
                        <p className="text-gray-700">
                          <span className="font-medium">Inicio:</span> {formatDate(property.contrato_activo.fecha_inicio)}
                        </p>
                        <p className="text-gray-700">
                          <span className="font-medium">Fin:</span> {formatDate(property.contrato_activo.fecha_fin)}
                        </p>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="w-full mt-2"
                        onClick={() => navigate(`/contracts/${property.contrato_activo!.id}`)}
                      >
                        Ver Contrato
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Status & Availability */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Disponibilidad</h3>
              <div className="space-y-4">
                <div className={`p-4 rounded-lg border ${getStatusColor(property.estado)}`}>
                  <div className="flex items-center gap-2 justify-center">
                    <span className="text-2xl">{getStatusIcon(property.estado)}</span>
                    <span className="font-semibold">{property.estado}</span>
                  </div>
                </div>

                {property.estado === InmuebleEstado.LIBRE && hasPermission('contracts', 'write') && (
                  <Button
                    className="w-full"
                    onClick={() => navigate(`/contracts/new?inmueble_id=${id}`)}
                  >
                    Crear Contrato
                  </Button>
                )}

                {property.estado === InmuebleEstado.ALQUILADO && hasPermission('contracts', 'write') && (
                  <Button
                    className="w-full"
                    variant="secondary"
                    onClick={() => setShowHandoverModal(true)}
                  >
                    Entregar Inmueble
                  </Button>
                )}
              </div>
            </div>

            {/* Metadata Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Información del Sistema</h3>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-gray-500">ID</p>
                  <p className="font-mono text-gray-900">#{property.id}</p>
                </div>
                {property.created_at && (
                  <div>
                    <p className="text-gray-500">Creado</p>
                    <p className="text-gray-900">{formatDate(property.created_at)}</p>
                  </div>
                )}
                {property.updated_at && (
                  <div>
                    <p className="text-gray-500">Última actualización</p>
                    <p className="text-gray-900">{formatDate(property.updated_at)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Property Handover Modal */}
        <Modal
          isOpen={showHandoverModal}
          onClose={() => setShowHandoverModal(false)}
          title="Entregar Inmueble"
          size="md"
        >
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                Esta acción finalizará el contrato activo y marcará el inmueble como disponible.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Entrega <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={handoverForm.fecha_entrega}
                onChange={(e) => setHandoverForm({ ...handoverForm, fecha_entrega: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notas
              </label>
              <textarea
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                value={handoverForm.notas}
                onChange={(e) => setHandoverForm({ ...handoverForm, notas: e.target.value })}
                placeholder="Notas sobre la entrega del inmueble..."
              />
            </div>

            <div className="flex gap-3 justify-end pt-4">
              <Button
                variant="secondary"
                onClick={() => setShowHandoverModal(false)}
                disabled={submitting}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleHandoverSubmit}
                disabled={submitting || !handoverForm.fecha_entrega}
              >
                {submitting ? 'Procesando...' : 'Confirmar Entrega'}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </MainLayout>
  );
}


